import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EntityType, QueryFilter } from '@mariana/labos-client';
import type { ColumnDef } from '../config/viewsConfig';
import { useEntityList } from './useEntityList';
import type { UseEntityListResult } from './useEntityList';
import { filtersEqual } from '../utils/filtersEqual';
import { writeColumns } from '../utils/persistedColumns';
import { resolveColumnOrderFromKeys } from '../utils/resolveColumnFromUrlKey';
import { usePersistedColumns } from './usePersistedColumns';

interface UseViewStateOptions {
  entityType: EntityType;
  allColumns: ColumnDef[];
  /**
   * Curated columns offered in the picker but not visible by default. Added to
   * the resolution universe so they persist / restore from shared URLs.
   */
  optionalColumns?: ColumnDef[];
  initialFilters?: QueryFilter[];
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  viewId?: string;
  disabled?: boolean;
  extraFilters?: QueryFilter[];
}

export interface UseViewStateResult extends UseEntityListResult {
  visibleColumns: ColumnDef[];
  setVisibleColumns: (cols: ColumnDef[]) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  isDirty: boolean;
  allColumns: ColumnDef[];
}

function columnsMatchDefault(visible: ColumnDef[], all: ColumnDef[]): boolean {
  if (visible.length !== all.length) return false;
  for (let i = 0; i < visible.length; i++) {
    if (visible[i].key !== all[i].key) return false;
  }
  return true;
}

export function useViewState({
  entityType,
  allColumns,
  optionalColumns,
  initialFilters = [],
  defaultSort,
  viewId,
  disabled = false,
  extraFilters,
}: UseViewStateOptions): UseViewStateResult {
  const [searchParams, setSearchParams] = useSearchParams();
  // Wider universe used to resolve persisted/URL keys (includes optional
  // columns); default-visible set stays `allColumns`.
  const optionalColumnsKey = optionalColumns?.map(c => c.key).join(',') ?? '';
  const knownColumns = useMemo(
    () => (optionalColumns?.length ? [...allColumns, ...optionalColumns] : allColumns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumns, optionalColumnsKey],
  );
  const { columns: persistedColumns, setColumns: persistColumns, storageKey } = usePersistedColumns(
    viewId ?? entityType, allColumns, entityType, knownColumns,
  );
  const [visibleColumns, setVisibleColumnsState] = useState(allColumns);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Derive visible columns from the URL `cols` param (falling back to the
  // persisted set). `colsParam` MUST be a dependency: the effect reads it, and
  // omitting it makes the effect run against a stale URL when setVisibleColumns'
  // batched state + URL updates land across separate commits (which silently
  // reverted column removals until a second apply).
  const colsParam = searchParams.get('cols');
  useEffect(() => {
    if (colsParam) {
      const keys = colsParam.split(',').map(k => k.trim()).filter(Boolean);
      const restored = resolveColumnOrderFromKeys(keys, entityType, knownColumns);
      setVisibleColumnsState(restored.length > 0 ? restored : allColumns);
    } else {
      setVisibleColumnsState(persistedColumns);
    }
    setSearchQuery('');
    setDebouncedSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, colsParam, persistedColumns]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const setVisibleColumns = useCallback((cols: ColumnDef[]) => {
    setVisibleColumnsState(cols);
    persistColumns(cols);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (columnsMatchDefault(cols, allColumns)) {
        next.delete('cols');
      } else {
        next.set('cols', cols.map(c => c.key).join(','));
      }
      return next;
    }, { replace: true });
  }, [allColumns, persistColumns, setSearchParams]);

  const entityList = useEntityList({
    entityType,
    columns: allColumns,
    initialFilters,
    defaultSort,
    viewId,
    disabled,
    search: debouncedSearch || undefined,
    extraFilters,
  });

  const defaultSortBy = defaultSort?.by ?? 'id';
  const defaultSortOrder = defaultSort?.order ?? 'desc';

  const isDirty = useMemo(() => {
    if (!filtersEqual(entityList.filters, initialFilters)) return true;
    if (entityList.sortBy !== defaultSortBy || entityList.sortOrder !== defaultSortOrder) return true;
    if (!columnsMatchDefault(visibleColumns, allColumns)) return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityList.filters, initialFilters, entityList.sortBy, entityList.sortOrder, visibleColumns, allColumns, defaultSortBy, defaultSortOrder]);

  return {
    ...entityList,
    visibleColumns,
    setVisibleColumns,
    searchQuery,
    setSearchQuery,
    isDirty,
    allColumns,
  };
}
